import type {
  HWPXDocument,
  DocumentMeta,
  DocumentHead,
  Section,
  SectionDef,
  CharProperty,
  ParaProperty,
  FontFace,
  Style,
  BorderFill,
  FontRef,
  PageMargin,
} from './types.js';
import { mmToHwpunit, ptToCharHeight } from './utils/units.js';
import { hexToColorref } from './utils/color.js';

// ─── 기본 폰트 참조 (모든 언어에 index 0) ───

function defaultFontRef(): FontRef {
  return { hangul: 0, latin: 0, hanja: 0, japanese: 0, other: 0, symbol: 0, user: 0 };
}

// ─── 기본 폰트 페이스 ───

export function createDefaultFontFaces(): FontFace[] {
  return [
    { lang: 'HANGUL', fontName: '함초롬돋움', fontType: 'ttf' },
    { lang: 'LATIN', fontName: '함초롬돋움', fontType: 'ttf' },
    { lang: 'HANJA', fontName: '함초롬돋움', fontType: 'ttf' },
    { lang: 'JAPANESE', fontName: '함초롬돋움', fontType: 'ttf' },
    { lang: 'OTHER', fontName: '함초롬돋움', fontType: 'ttf' },
    { lang: 'SYMBOL', fontName: '함초롬돋움', fontType: 'ttf' },
    { lang: 'USER', fontName: '함초롬돋움', fontType: 'ttf' },
  ];
}

// ─── 기본 글자 속성 ───

export function createDefaultCharProperty(id = 0): CharProperty {
  return {
    id,
    height: ptToCharHeight(10), // 10pt
    textColor: hexToColorref('#000000'),
    bold: false,
    italic: false,
    underline: 'NONE',
    strikeout: 'NONE',
    fontRef: defaultFontRef(),
    useFontSpace: false,
    useKerning: true,
    relSize: 100,
    spacing: 0,
    charOffset: 0,
  };
}

// ─── 기본 문단 속성 ───

export function createDefaultParaProperty(id = 0): ParaProperty {
  return {
    id,
    alignment: 'JUSTIFY',
    lineSpacing: { type: 'PERCENT', value: 160 },
    paraMargin: {
      left: 0,
      right: 0,
      indent: 0,
      prevSpacing: 0,
      nextSpacing: 0,
    },
  };
}

// ─── 기본 테두리/배경 ───

export function createDefaultBorderFill(id = 1): BorderFill {
  const noBorder = {
    type: 'NONE' as const,
    width: '0.1mm' as const,
    color: hexToColorref('#000000'),
  };
  return {
    id,
    borders: {
      left: { ...noBorder },
      right: { ...noBorder },
      top: { ...noBorder },
      bottom: { ...noBorder },
    },
  };
}

// ─── 기본 스타일 ───

export function createDefaultStyles(): Style[] {
  return [
    {
      id: 0,
      type: 'PARA',
      name: '바탕글',
      engName: 'Normal',
      paraPrIDRef: 0,
      charPrIDRef: 0,
      nextStyleIDRef: 0,
    },
  ];
}

// ─── 기본 페이지 설정 (A4) ───

export function createDefaultSectionDef(): SectionDef {
  return {
    pageWidth: mmToHwpunit(210),
    pageHeight: mmToHwpunit(297),
    pageMargin: createDefaultPageMargin(),
    landscape: false,
    gutterType: 'LEFT_ONLY',
  };
}

export function createDefaultPageMargin(): PageMargin {
  return {
    left: mmToHwpunit(30),
    right: mmToHwpunit(30),
    top: mmToHwpunit(25),
    bottom: mmToHwpunit(25),
    header: mmToHwpunit(15),
    footer: mmToHwpunit(15),
    gutter: 0,
  };
}

// ─── 기본 문서 Head ───

export function createDefaultHead(): DocumentHead {
  return {
    fontFaces: createDefaultFontFaces(),
    charProperties: [createDefaultCharProperty(0)],
    paraProperties: [createDefaultParaProperty(0)],
    styles: createDefaultStyles(),
    borderFills: [createDefaultBorderFill(1)],
    bulletProperties: [],
    numberingProperties: [],
    compatibleDoc: 'HWP',
  };
}

// ─── 기본 문서 Meta ───

export function createDefaultMeta(): DocumentMeta {
  return {
    hwpVersion: '5.1.0.1',
  };
}

// ─── 기본 빈 문서 ───

export function createDefaultDocument(): HWPXDocument {
  return {
    meta: createDefaultMeta(),
    head: createDefaultHead(),
    sections: [
      {
        def: createDefaultSectionDef(),
        paragraphs: [],
      },
    ],
    binData: [],
  };
}
